import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { PHONE_DIALOG_FOOTER, PHONE_SHEET_DIALOG } from "@/lib/responsive";

/** Shared "Cancel / confirm this action" dialog shape - title, description,
 *  an optional middle slot (an error message, an input, or nothing), and a
 *  Cancel + primary action pair whose label swaps to a loading state while
 *  pending. Used by MyApprovals' approve/reject decision and User
 *  Management's grant/revoke-role confirm - both are this same shape with
 *  different copy and a different middle slot, not different dialogs. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel,
  pendingLabel = "Saving…",
  onConfirm,
  pending = false,
  destructive = false,
  confirmDisabled = false
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Rendered between the description and the footer - an error message,
   *  an input, or nothing. */
  children?: React.ReactNode;
  confirmLabel: React.ReactNode;
  pendingLabel?: string;
  onConfirm: () => void;
  pending?: boolean;
  /** Uses the destructive button variant for the confirm action (e.g.
   *  "Remove access", "Reject") instead of the default variant. */
  destructive?: boolean;
  /** Extra condition (on top of `pending`) that disables the confirm
   *  button - e.g. an edit dialog passing `!isDirty` so Save stays
   *  disabled until the caller's own form actually changed from what was
   *  loaded. Defaults to false (no extra gating) so every existing caller
   *  is unaffected. */
  confirmDisabled?: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={PHONE_SHEET_DIALOG}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        <DialogFooter className={PHONE_DIALOG_FOOTER}>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant={destructive ? "destructive" : "default"}
            onClick={onConfirm}
            disabled={pending || confirmDisabled}
          >
            {pending ? pendingLabel : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
